import { Editor } from "@tiptap/core";
import { buildRoomName, sectionField } from "@/collab/roomName";
import { buildEditorExtensions } from "@/components/tiptap/editorExtensions";
import { parseContent } from "@/components/tiptap/parseContent";
import { DocumentSessionManager, type SessionHandle } from "./DocumentSessionManager";
import { currentRoomParts, findSectionByDocId } from "./workProductContext";
import { documentSessionManager } from "./documentSessionManagerSingleton";

export type HeadlessEditorResult =
    | { status: "ok"; editor: Editor }
    | { status: "failed"; reason: "doc_not_open" | "doc_unavailable" };

/**
 * Agent-owned headless TipTap peers, one per section fragment. The mounted
 * editor is never touched: marks staged here reach it over Yjs. Editors and
 * the agent session handle live until the manager destroys the session
 * (idle TTL after the last release).
 */
export class HeadlessSectionEditors {
    private editors = new Map<string, Editor>(); // key: fragment field
    private handle: SessionHandle | null = null;
    /**
     * Room name of the most recently acquired handle. Kept even after
     * `releaseAll()` nulls `handle`: the manager's idle-TTL destroy for that
     * room fires later, asynchronously, and `onSessionDestroyed` below needs
     * something to compare against besides `this.handle` (which is already
     * gone by then) to recognize that eventual notification as its own.
     */
    private handleRoomName: string | null = null;
    private syncTimeoutMs: number;

    constructor(private manager: DocumentSessionManager, deps?: { syncTimeoutMs?: number }) {
        this.syncTimeoutMs = deps?.syncTimeoutMs ?? 15_000;
        manager.onSessionDestroyed(roomName => {
            if (this.handleRoomName !== roomName) return;
            this.editors.forEach(e => e.destroy());
            this.editors.clear();
            this.handle = null;
            this.handleRoomName = null;
        });
    }

    /** Release the agent ref (e.g. on workproduct close); editors die when the session does. */
    releaseAll(): void {
        this.handle?.release();
        this.handle = null;
        // handleRoomName intentionally NOT cleared here: the room isn't destroyed
        // yet (idle TTL hasn't elapsed), and the listener above still needs it.
    }

    async getEditor(docId: string): Promise<HeadlessEditorResult> {
        const section = findSectionByDocId(docId);
        if (!section) return { status: "failed", reason: "doc_not_open" };
        const parts = currentRoomParts();
        if (!parts) return { status: "failed", reason: "doc_unavailable" };

        if (!this.handle) {
            this.handle = this.manager.acquireAgent(parts);
            if (!this.handle) return { status: "failed", reason: "doc_unavailable" };
            this.handleRoomName = this.handle.session.roomName;
        } else {
            // Compare against the target room WITHOUT acquiring a ref — acquireAgent
            // increments agentRefs on every call, so probing with it here would leak
            // one ref per same-room getEditor() call and the session would never
            // hit zero refs (never idle-TTL out).
            const targetRoom = buildRoomName(parts);
            if (this.handle.session.roomName !== targetRoom) {
                // Workproduct switched: acquire the new room's session ONCE, then
                // release the old one and tear down editors bound to its fragments.
                // Check `fresh` FIRST: a failed acquire (transient token/url
                // unavailability) must leave the still-valid old handle/editors/
                // handleRoomName completely untouched rather than tearing them down
                // and returning nothing usable.
                const fresh = this.manager.acquireAgent(parts);
                if (!fresh) return { status: "failed", reason: "doc_unavailable" };
                this.handle.release();
                this.editors.forEach(e => e.destroy());
                this.editors.clear();
                this.handle = fresh;
                this.handleRoomName = fresh.session.roomName;
            }
        }

        const synced = await this.manager.whenSynced(this.handle.session, this.syncTimeoutMs);
        if (!synced) return { status: "failed", reason: "doc_unavailable" };

        const field = sectionField(section.warehouseId);
        let editor = this.editors.get(field);
        if (!editor) {
            const fragment = this.handle.session.ydoc.getXmlFragment(field);
            editor = new Editor({
                element: document.createElement("div"),
                extensions: buildEditorExtensions({ mode: "headless", collab: { fragment } })
            });
            this.editors.set(field, editor);
            this.seedIfNeeded(editor, field, section.contentHtml);
        }
        return { status: "ok", editor };
    }

    /**
     * Same guarded seeding as TiptapEditor.tsx's collab seeding effect, minus the
     * election: the agent defers to any mounted peer advertising this sectionField
     * and only seeds when it is alone on an un-seeded, empty fragment.
     */
    private seedIfNeeded(editor: Editor, field: string, contentHtml: string): void {
        const session = this.handle?.session;
        if (!session) return;
        const meta = session.ydoc.getMap<boolean>("collabMeta");
        const seededKey = `seeded:${field}`;
        const fragment = session.ydoc.getXmlFragment(field);
        if (meta.get(seededKey) || fragment.length > 0) return;
        const states = session.provider.awareness?.getStates();
        const mountedPeerViewing = states
            ? [...states.values()].some(st => st?.sectionField === field)
            : false;
        if (mountedPeerViewing) return; // the mounted peer's election will seed
        editor.commands.setContent(parseContent(contentHtml), false);
        meta.set(seededKey, true);
    }
}

let _instance: HeadlessSectionEditors | null = null;

/**
 * Lazy singleton accessor. The codebase is pure ESM ("type": "module", no
 * `require`), so a top-level-await singleton export isn't usable from a sync
 * accessor. A direct static import of documentSessionManagerSingleton is
 * safe here — that module does not import HeadlessSectionEditors (or
 * anything that does), so there's no cycle; construction is simply deferred
 * to first use.
 */
export function getHeadlessSectionEditors(): HeadlessSectionEditors {
    if (!_instance) {
        _instance = new HeadlessSectionEditors(documentSessionManager);
    }
    return _instance;
}
